import React, { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { Button } from "@app/scripts/react-general-components/Button";
import { useShopify, useShopifyProduct } from "@app/scripts/react-hooks/shopify";

import type { NormalisedProduct, NormalisedVariant, Product } from "@app/scripts/types";

type Props = {
  product?: NormalisedProduct | Product;
  className?: string;
};

export const QuickAddPlp: React.FC<Props> = ({ product, className }) => {
  const { normaliseProduct } = useShopify();
  const normalisedProduct = useMemo(
    () =>
      (product as NormalisedProduct)?.is_normalised
        ? (product as NormalisedProduct)
        : normaliseProduct(product as Product),
    [product, normaliseProduct]
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);

  const { error, handleAddToCart, handleChangeVariant, selectedVariantId, setSelectedVariantId, url } =
    useShopifyProduct({
      normalisedProduct,
    });

  const addToCart = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      const success = await handleAddToCart(e);
      if (success) {
        setIsSuccess(true);
      }
    },
    [handleAddToCart]
  );

  const singleVariantAdd = useCallback(
    async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e.preventDefault();
      setSelectedVariantId(normalisedProduct.variants?.[0].id);
      if (e.target.classList.contains("disabled")) {
        return (window.location.href = `/products/${product?.handle}`);
      }
      const success = await handleAddToCart(e, normalisedProduct.variants?.[0].id);
      if (success) setIsSuccess(true);
    },
    [isOpen, setIsOpen, handleAddToCart]
  );

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChangeVariant(e);
      if (e.target.classList.contains("disabled")) {
        return (window.location.href = `/products/${product?.handle}?variant=${e.target.value}`);
      }
      const success = await handleAddToCart(e, e.target.value);

      const selectedVariantObject = normalisedProduct?.variants?.find((variant) => {
        return variant.id === Number(e.target.value);
      });

      setSelectedVariant(selectedVariantObject?.title || null);

      if (success) setIsSuccess(true);
    },
    [selectedVariantId, error, handleChangeVariant]
  );

  const gridColumn =
    product.variants.length >= 7
      ? "product-card__plp-quick-add-variants grid-cols-7"
      : "product-card__plp-quick-add-variants grid-cols-" + product.variants.length;

  // Sorting variants to display correct order when nested
  const sortOrder = ["000", "OOO", "00", "OO", "0", "O", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
  const sortedVariants = _.sortBy(product?.variants, (obj) => {
    const title = obj.title;
    // check sortOrder and return index
    if (sortOrder?.includes(title)) return sortOrder.indexOf(title);

    return parseInt(title) + 99;
  });

  // Function added specifically for Indie Kids, this will check if the product has multiple variants and if the titles match
  // This happens due to the way products are set up in the backend for Indie Kids specifically 
  function checkVariantTitles(product) {
    if (product?.length === 1) {
      return true;
    }
    const firstTitle = product[0].title;
    return product.every(item => item?.title === firstTitle);
  }

  let indieKidsCheckVariantLength = checkVariantTitles(product?.variants);
  console.log({ product, sortedVariants, selectedVariantId, error, isSuccess, indieKidsCheckVariantLength });

  return !isSuccess ? (
    <form
      method="post"
      action="/cart/add"
      onSubmit={addToCart}
      className={`product-card__plp-quick-add ${className || ""}`}
    >
      <input name="__id" value={selectedVariantId} type="hidden" />
      {error && (
        <small className="product-card__plp-quick-add-error bg-brand-warning text-brand-white">
          {error.includes("All 1")
            ? `Only one left! The last ${normalisedProduct.title} in ${selectedVariant} is in your cart.`
            : error}
        </small>
      )}
      {product?.variants?.length === 1 || indieKidsCheckVariantLength ? (
        <Button
          label="Quick Add"
          ariaLabel="Quick Add"
          color="white"
          variant="secondary"
          className="swiper-no-swiping product-card__plp-quick-add-button"
          onClick={singleVariantAdd}
        />
      ) : (
        <div className={gridColumn}>
          {product?.variants &&
            product.variants.length > 1 &&
            (sortedVariants as NormalisedVariant[])?.map((variant, index) => {
              return (
                <div className="product-card__plp-quick-add-item">
                  {variant.currentlyNotInStock === true && (
                    <small
                      data-pos={index % 7}
                      className="product-card__plp-quick-add-oos bg-secondary text-secondary-grey"
                    >
                      Out Of Stock - Notify Me
                    </small>
                  )}
                  <label data-disabled={!variant.available || variant.currentlyNotInStock === true}>
                    <input
                      type="radio"
                      name="id"
                      className={`product-card__plp-quick-add-variants-button${!variant.available || variant.currentlyNotInStock === true ? " disabled" : ""
                        }`}
                      onChange={handleChange}
                      value={variant.id}
                    />
                    {variant.title.replace(".00", "")}
                  </label>
                </div>
              );
            })}
        </div>
      )}
      <button type="submit" className="hidden" />
    </form>
  ) : null;
};
